import { Component, Input, OnDestroy, OnInit , signal } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, lastValueFrom, map, Observable, Subscription, switchMap } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { Riser } from './riser.model';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { Tubular } from '../well-configuration/models';
import { WellTypeService } from '../../services/well-type-datums.service';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { GetInchesFromValue, GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';

@Component({
  selector: 'app-riser-component',
  templateUrl: './riser-component.component.html',  
  standalone: false
})
export class RiserComponentComponent implements OnInit, OnDestroy {
  private _subscriptions = new Subscription();
  private _calledFromSignalR = false;
  private _riserId: string;
  private _designId: string;
  
  public pipeRoughnessUnit: string;
  public isLoading = signal<boolean>(false);
  public disabledMessage: string;
  public configMissing = false;
  public risersForm: UntypedFormGroup;
  public tubulars: Tubular[];
  public riserIncluded: boolean;
  
  @Input() private componentId: string;

  constructor(
    private _store: StoreService,
    private _signalRService: SignalRService,
    private _wellConfigService: WellConfigService,
    private _messenger: MediatorService,
    private _formBuilder: UntypedFormBuilder,
    private _wellTypeService: WellTypeService
  ) {
    this.risersForm = this._formBuilder.group({
      tubularStringId: new UntypedFormControl(null),
      designId: new UntypedFormControl(null),
      includeRiser: new UntypedFormControl(false),
      riser: new FormGroup({
        outsideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
        insideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
        attachedTubularId: new UntypedFormControl(null),
      })
    });
  }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    const units = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);
    const appIds = this._store.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this._designId = appIds?.designId;
    this.pipeRoughnessUnit = units.shortLengths;

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
    this.getData();

    if (!this._calledFromSignalR) {
      const debouncedOnChange = this.risersForm.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
      );
      this._subscriptions.add(debouncedOnChange.subscribe(formData => this.onFormChange(formData)));
    }
  }

  private signalRfunc(data: any): void {
    if ([PeriforOnChangeMessages.REFRESH_PIPES_CATALOG, PeriforOnChangeMessages.REFRESH_STRING_INPUTS, PeriforOnChangeMessages.REFRESH_TUBULARS_LIST].includes(data.action)) {
      this.risersForm.reset({ emitEvent: false });
      this.isLoading.set(true);
      this._calledFromSignalR = true;
      this.getData();
    }
  }

private getData(): void {
  this.isLoading.set(true);
  this.configMissing = false;
  this.disabledMessage = '';

  const primarySources: Observable<any>[] = [
    this._wellConfigService.getTubular(),
    this._wellTypeService.getWellType()
  ];

  forkJoin(primarySources).pipe(
    switchMap(([currentString, wellType]) => {
      if (!currentString?.id) {
        this.configMissing = true;
        this.disabledMessage = 'Well Configuration Missing: Please specify at least one string';
        this.isLoading.set(false);
        this._calledFromSignalR = false;
        return;
      }

      if (wellType.type != 'Subsea') {
        this.configMissing = true;
        this.disabledMessage = 'Risers are only applicable for Subsea well types';
        this.isLoading.set(false);
        this._calledFromSignalR = false;
        return;
      }

      if (wellType.type == 'Subsea' && currentString.sequenceNumber === 0) {
        this.configMissing = true;
        this.disabledMessage = 'Risers are only applicable for inner strings of a Subsea well type';
        this.isLoading.set(false);
        this._calledFromSignalR = false;
        return;
      }

      const sources: Observable<any>[] = [
        this._wellConfigService.getTubulars(),
        this._wellConfigService.getRiserForTubular()
      ];

      return forkJoin(sources).pipe(
        map(([tubulars, riser]) => ({ currentString, tubulars, riser }))
      );
    }),
    catchError(err => {
      this.isLoading.set(false);
      this._calledFromSignalR = false;
      throw err;
    })
  )
  .subscribe(({ currentString, tubulars, riser }) => {
    const currentStringIndex = tubulars.findIndex((t: any) => t.id === currentString.id);

    this.risersForm.controls.tubularStringId.setValue(currentString.id, { emitEvent: false } );
    this.risersForm.controls.designId.setValue(this._designId, { emitEvent: false } );

    this._riserId = riser?.id;

    this.tubulars = tubulars.filter(x => x.sequenceNumber < currentStringIndex).map((t: any) => ({ label: t.name, value: t.id }));

    this.riserIncluded = riser?.includeRiser ?? false;

    const pipeOD = currentString.stringSections?.[0]?.pipe?.outsideDiameter;
    const defaultRiserOD = GetInchesFromValue(pipeOD, this.pipeRoughnessUnit) >= 20 ? GetShortLengthValueFromInches(28, this.pipeRoughnessUnit) : GetShortLengthValueFromInches(21, this.pipeRoughnessUnit);
    const defaultRiserID = GetInchesFromValue(pipeOD, this.pipeRoughnessUnit) >= 20 ? GetShortLengthValueFromInches(26.5, this.pipeRoughnessUnit) : GetShortLengthValueFromInches(19.25, this.pipeRoughnessUnit);

    this.risersForm.patchValue(
      riser.riser?.outsideDiameter ? riser : {
        tubularStringId: currentString.id,
        designId: this._designId,
        includeRiser: false,
        riser: {
          outsideDiameter: defaultRiserOD,
          insideDiameter: defaultRiserID,
          attachedTubularId: null
        }
      }, { emitEvent: false }
    );

    this.isLoading.set(false);
    this._calledFromSignalR = false;
  });
}

  private async onFormChange(formData: Riser): Promise<void> {
    if (this.risersForm.pristine) {
      return;
    }

    if (this.risersForm.valid && !this.isLoading()) {
      if (this._riserId) {
        formData.id = this._riserId;
        await lastValueFrom(this._wellConfigService.updateRiserForTubular(formData));
      } else {
        const riser = await lastValueFrom(this._wellConfigService.addRiser(formData));
        this._riserId = riser.id;
      }
      this._messenger.publish(new DataSavedMessage('Risers'));
    }
  }

  public hasRiserChange(): void {
    this.riserIncluded = this.risersForm.controls.includeRiser.value;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this.signalRfunc = null;
  }
}
